/**
 * Source Control state — the toggle model behind the Evidence source picker.
 *
 * Extracted from `chatAtoms.ts` VERBATIM. This state is not Chat's: measured at
 * extraction, TEN production files outside Chat read these identities and only
 * these, three of them in the evidence-owned Library tree, and six of them WRITE
 * `sourceControlToggleAtom`. Its two sibling modules — `sourceControlSelectors.ts`
 * and `sourceControlPersistence.ts` — already lived here and had to reach back
 * into the chat module for the shape they select over.
 *
 * No re-export is left in `chatAtoms.ts`. A re-export would keep every reader
 * resolving through the one module the Chat quarantine watches by name, which is
 * the coupling this move removes.
 */

import { IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED } from '@/components/constants/flags';
import {
  SOURCE_CONTROL_TOGGLE_STORAGE_KEY,
  type EvidenceSourceFilters,
} from '@heidi/evidence/contracts';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { applyChatSourceToggleGuard } from './chatSourceRequireOne';
import { createSourceControlToggleStorage } from './sourceControlLocalStorage';

// ===== EVI-405: Source Control State for Chat Input =====

/**
 * Source control toggle state for the simplified Ask Heidi input.
 * Tracks whether local/regional guidelines, Primary evidence, and Library are active.
 *
 * Note: `localGuidelines` is region-agnostic - the display label
 * (e.g., "Australian guidelines", "American guidelines") is determined
 * at render time based on the user's region.
 *
 * Library uses a menu item pattern (not a toggle) with collection selection:
 * - `myLibrary`: Whether Library search is enabled
 * - `libraryMode`: 'all' searches all docs, 'collections' uses selectedCollectionIds
 * - `selectedCollectionIds`: Specific collections to search (when libraryMode is 'collections')
 */
export interface SourceControlToggleState {
  localGuidelines: boolean;
  primaryEvidence: boolean;
  myLibrary: boolean;
  libraryMode: 'all' | 'collections';
  selectedCollectionIds: string[];
  // Web collections (source lists) - domains from user's curated web sources
  webCollections: boolean;
  webCollectionsMode: 'all' | 'collections';
  selectedWebCollectionIds: string[];
  // EVI-678: Integration partners - map of partnerId -> enabled
  integrationPartners: Record<string, boolean>;
  // EVI-721: Explicit web search toggle (used by Source Control V2)
  webSearch: boolean;
  // EVI-721: Include documents not assigned to any collection (sends __uncollected__ to BE)
  includeUncollected: boolean;
}

/**
 * localStorage key backing `sourceControlToggleAtom`, declared in
 * `@heidi/evidence/contracts` because a persisted key is a cache identity and
 * identities live behind a domain door. Re-exported here so the readers that
 * already take it off this module keep resolving — the ban is on a second
 * DECLARATION, not on passing the identity along. Cleared from
 * `clearLogoutStorage` on full logout (see `src/utils/auth/logoutStorage.ts`).
 */
export { SOURCE_CONTROL_TOGGLE_STORAGE_KEY };

/**
 * Default source control toggle values.
 * Exported for use in tests and potential "reset to defaults" actions.
 */
export const SOURCE_CONTROL_DEFAULTS: SourceControlToggleState = {
  localGuidelines: false,
  primaryEvidence: false,
  myLibrary: false,
  libraryMode: 'all',
  selectedCollectionIds: [],
  webCollections: false,
  webCollectionsMode: 'all',
  selectedWebCollectionIds: [],
  integrationPartners: {},
  webSearch: true,
  includeUncollected: false,
};

/**
 * Atom for source control toggle state.
 *
 * EVI-1324: persisted server-side under `User.evidence_settings.source_control`
 * so selections follow the user across devices. localStorage is kept as a
 * warm cache via `atomWithStorage`; its value arrives after mount because this
 * atom deliberately keeps Jotai's `getOnInit: false` default. The submission
 * path waits on `sourceControlReadinessAtom` until the cache or canonical BE
 * value is safe to use. `useSyncSourceControlSelections` overrides the atom
 * from the BE on every fresh login and debounces atom changes back into
 * PATCH /v1/user/ writes.
 * The localStorage entry is cleared on full logout (see `clearLogoutStorage`).
 *
 * Because the BE is canonical, the cache write is allowed to fail: the storage
 * adapter swallows a full-origin `QuotaExceededError` and repairs blobs left by
 * older builds on read (see `sourceControlLocalStorage.ts`).
 */
export const sourceControlToggleAtom = atomWithStorage<SourceControlToggleState>(
  SOURCE_CONTROL_TOGGLE_STORAGE_KEY,
  SOURCE_CONTROL_DEFAULTS,
  createSourceControlToggleStorage(SOURCE_CONTROL_DEFAULTS)
);

export type SourceControlReadiness =
  | { status: 'pending' }
  | { status: 'blocked' }
  | { status: 'ready'; source: 'local' }
  | { status: 'ready'; source: 'anonymous' }
  /**
   * INC-349 / DEV-12867: written only by the fail-open watchdog
   * (`useSourceControlReadinessFailOpen`) when readiness has been stuck in
   * `pending` past the deadline — no auth shell published a lifecycle at all,
   * or one did and hydration has not resolved yet. Provisional: the writer
   * overrides it with a hydrated value whenever it does resolve. Sends made
   * under fail-open use `SOURCE_CONTROL_DEFAULTS`, never the cached toggle
   * atom — the cache is not user-scoped and survives same-tab account
   * switches (access-control review on #19625).
   */
  | { status: 'ready'; source: 'fail-open' }
  | { status: 'ready'; source: 'backend'; userId: string };

/**
 * Baked default for how long readiness may stay `pending` before consumers
 * stop waiting and fail open. Both deadlines (the watchdog and the send-path
 * wait in `useChatStream`) resolve through
 * `getSourceControlFailOpenTimeoutMs`, which applies a clamped Flagsmith
 * override on top of this default — remote-tunable without a desktop
 * release, but structurally impossible to turn into a kill switch. The gate
 * must never fail closed: a missing writer disabled the composer for every
 * non-standard auth surface in INC-349.
 */
export const SOURCE_CONTROL_READINESS_TIMEOUT_MS = 10_000;

export const sourceControlReadinessAtom = atom<SourceControlReadiness>({
  status: 'pending',
});

/**
 * True while a `useSyncSourceControlSelections` writer is mounted (the hook
 * registers itself). Reported on every fail-open event as context. It is no
 * longer the structural discriminator: the single writer now mounts in
 * `AuthWrapper` above every auth branch, so it is true on any path that
 * reaches the deadline. The identity of `sourceControlSyncLifecycleAtom`
 * carries that job now. Never gates behavior — fail-open fires either way.
 */
export const sourceControlWriterMountedAtom = atom<boolean>(false);

/**
 * Auth and user-query state the readiness writer hydrates from.
 *
 * The writer mounts once above every auth-branch return AND above the
 * workspace-hydration gate, so it cannot read a branch's own auth hooks: each
 * shell publishes its view here and the single writer consumes it. Holding the
 * writer above the gate is the point — the gate unmounts the branch that used
 * to own it for the whole cold-boot window, which is why a session with a
 * perfectly healthy shell could still reach the fail-open deadline with no
 * writer mounted at all.
 */
export interface SourceControlSyncLifecycle {
  isAuthenticated: boolean;
  isAuthenticationLoading: boolean;
  hasFreshUserData: boolean;
  isUserQueryError: boolean;
}

/**
 * Value held until a shell publishes. It reads as "auth still resolving", which
 * keeps readiness `pending` — the honest answer while no shell has a verdict,
 * and never a guess that could resolve readiness against the wrong user.
 *
 * Its IDENTITY is also the fail-open telemetry's discriminator. Nothing ever
 * writes this object back, and `usePublishSourceControlSyncLifecycle` always
 * writes a fresh literal, so `lifecycle !== SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING`
 * answers "has any auth shell reported in yet?" without a second atom to keep
 * in step. See `reportSourceControlFailOpen` for what the two tiers mean.
 */
export const SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING: SourceControlSyncLifecycle = {
  isAuthenticated: false,
  isAuthenticationLoading: true,
  hasFreshUserData: false,
  isUserQueryError: false,
};

export const sourceControlSyncLifecycleAtom = atom<SourceControlSyncLifecycle>(
  SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING
);

export const isSourceControlReadyAtom = atom(
  (get) => get(sourceControlReadinessAtom).status === 'ready'
);

export const isSourceControlReadyForUser = (
  readiness: SourceControlReadiness,
  userId: string | null
): boolean => {
  if (readiness.status !== 'ready') return false;
  if (readiness.source === 'local') return true;
  // Fail-open readiness is deliberately user-agnostic: it exists precisely
  // for sessions where no writer resolved a user-scoped value (INC-349).
  // Safe despite skipping the per-user check because fail-open SENDS never
  // consume the cached toggle — useChatStream substitutes
  // SOURCE_CONTROL_DEFAULTS into the payload, so no other user's collection
  // IDs or partners can leave the device during the window.
  if (readiness.source === 'fail-open') return true;
  if (readiness.source === 'anonymous') return userId === null;
  return readiness.userId === userId;
};

/**
 * Derived atom that computes the current SourcesSelected value from toggle state.
 */
export const sourcesSelectedAtom = atom((get) => {
  const { localGuidelines, primaryEvidence, myLibrary, webCollections } = get(
    sourceControlToggleAtom
  );

  // Show combined sources when multiple are active
  if (myLibrary && webCollections) {
    return 'library + web collections' as const;
  }
  if (myLibrary) {
    return 'my library' as const;
  }
  if (webCollections) {
    return 'web collections' as const;
  }
  if (localGuidelines && primaryEvidence) {
    return 'evidence + guidelines' as const;
  }
  if (localGuidelines) {
    return 'local guidelines' as const;
  }
  if (primaryEvidence) {
    return 'primary evidence' as const;
  }
  return 'web search' as const;
});

/**
 * Derived atom that computes evidence source filters from toggle state.
 * Used by useChatStream to read the LATEST filters from the Jotai store
 * at stream-creation time, avoiding stale closure issues.
 *
 * - ['local_guidelines'] = Vector search only
 * - ['primary_evidence'] = Primary evidence web search only
 * - ['local_guidelines', 'primary_evidence'] = Both
 * - [] = Web search mode (all sources, no filtering)
 */
export const evidenceSourceFiltersAtom = atom<EvidenceSourceFilters>((get) => {
  const { localGuidelines, primaryEvidence } = get(sourceControlToggleAtom);
  const filters: EvidenceSourceFilters = [];

  if (localGuidelines) {
    filters.push('local_guidelines');
  }
  if (primaryEvidence) {
    filters.push('primary_evidence');
  }

  return filters;
});

/**
 * Derived atom that computes whether web search should be included.
 * Reads from explicit webSearch toggle (V2 source control).
 * Used by useChatStream to read the LATEST value from the Jotai store.
 */
export const includeWebSearchAtom = atom<boolean>((get) => {
  const toggle = get(sourceControlToggleAtom);
  // BE contract: when ALL sources are disabled and web search toggle is off,
  // force web search on to prevent an empty search configuration.
  // EVI-1957: when require-at-least-one is ON, do not silently force web —
  // the UI/write guards should prevent empty configs instead.
  const hasAnyIntegration = Object.values(toggle.integrationPartners).some(Boolean);
  const hasNoSources =
    !toggle.localGuidelines &&
    !toggle.primaryEvidence &&
    !toggle.myLibrary &&
    !toggle.webCollections &&
    !hasAnyIntegration;
  if (hasNoSources && !toggle.webSearch) {
    if (IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()) {
      return false;
    }
    return true;
  }
  return toggle.webSearch;
});

/**
 * Write-only atom to toggle local/regional guidelines.
 */
export const toggleLocalGuidelinesAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  const next = applyChatSourceToggleGuard(
    current,
    { ...current, localGuidelines: checked },
    IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
  );
  set(sourceControlToggleAtom, next);
});

/**
 * Write-only atom to toggle Primary evidence.
 */
export const togglePrimaryEvidenceAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  const next = applyChatSourceToggleGuard(
    current,
    { ...current, primaryEvidence: checked },
    IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
  );
  set(sourceControlToggleAtom, next);
});

/**
 * Write-only atom to toggle Practice Docs.
 * When turning off (checked=false), also clears selectedCollectionIds and resets libraryMode
 * to ensure the submenu state is in sync with the badge state.
 */
export const toggleMyLibraryAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  const proposed = checked
    ? { ...current, myLibrary: true }
    : {
        ...current,
        myLibrary: false,
        libraryMode: 'all' as const,
        selectedCollectionIds: [],
        includeUncollected: true,
      };
  set(
    sourceControlToggleAtom,
    applyChatSourceToggleGuard(
      current,
      proposed,
      IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
    )
  );
});

/**
 * Write-only atom to set library mode (all or collections).
 */
export const setLibraryModeAtom = atom(null, (get, set, mode: 'all' | 'collections') => {
  const current = get(sourceControlToggleAtom);
  set(sourceControlToggleAtom, { ...current, libraryMode: mode });
});

/**
 * Write-only atom to set selected collection IDs for library filtering.
 */
export const setSelectedCollectionIdsAtom = atom(
  null,
  (get, set, collectionIds: string[]) => {
    const current = get(sourceControlToggleAtom);
    set(sourceControlToggleAtom, {
      ...current,
      selectedCollectionIds: collectionIds,
      libraryMode: collectionIds.length > 0 ? 'collections' : 'all',
    });
  }
);

/**
 * Write-only atom to toggle inclusion of uncollected documents.
 * When true, '__uncollected__' is included in user_doc_collection_ids sent to the BE.
 */
export const toggleUncollectedAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  set(sourceControlToggleAtom, { ...current, includeUncollected: checked });
});

/**
 * Sentinel value for documents not assigned to any collection.
 */
export const UNCOLLECTED_COLLECTION_ID = '__uncollected__';

/**
 * Write-only atom to toggle Web Collections.
 * When turning off (checked=false), also clears selectedWebCollectionIds and resets webCollectionsMode.
 */
export const toggleWebCollectionsAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  const proposed = checked
    ? { ...current, webCollections: true }
    : {
        ...current,
        webCollections: false,
        webCollectionsMode: 'all' as const,
        selectedWebCollectionIds: [],
      };
  set(
    sourceControlToggleAtom,
    applyChatSourceToggleGuard(
      current,
      proposed,
      IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
    )
  );
});

/**
 * Write-only atom to set web collections mode (all or collections).
 */
export const setWebCollectionsModeAtom = atom(
  null,
  (get, set, mode: 'all' | 'collections') => {
    const current = get(sourceControlToggleAtom);
    set(sourceControlToggleAtom, { ...current, webCollectionsMode: mode });
  }
);

/**
 * Write-only atom to set selected web collection IDs for filtering.
 */
export const setSelectedWebCollectionIdsAtom = atom(
  null,
  (get, set, collectionIds: string[]) => {
    const current = get(sourceControlToggleAtom);
    set(sourceControlToggleAtom, {
      ...current,
      selectedWebCollectionIds: collectionIds,
      webCollectionsMode: collectionIds.length > 0 ? 'collections' : 'all',
    });
  }
);

// ===== Migration helpers (unified collections) =====

/**
 * Pure function to promote a legacy source-list ID to a collection ID in the toggle state.
 * Used after silent migration: removes from selectedWebCollectionIds, adds to selectedCollectionIds.
 */
export function promoteSelectedWebId(
  prev: SourceControlToggleState,
  sourceListId: string,
  collectionId: string
): SourceControlToggleState {
  const webIds = prev.selectedWebCollectionIds.filter((id) => id !== sourceListId);
  const docIds = prev.selectedCollectionIds.includes(collectionId)
    ? prev.selectedCollectionIds
    : [...prev.selectedCollectionIds, collectionId];

  return {
    ...prev,
    selectedWebCollectionIds: webIds,
    selectedCollectionIds: docIds,
    webCollections: webIds.length > 0 ? prev.webCollections : false,
    webCollectionsMode: webIds.length > 0 ? prev.webCollectionsMode : 'all',
    myLibrary: docIds.length > 0 ? true : prev.myLibrary,
    libraryMode: docIds.length > 0 ? 'collections' : prev.libraryMode,
  };
}

/**
 * Pure function to remove a deleted web collection from both atom arrays.
 * Distinct from promoteSelectedWebId — deletion removes from both, promotion moves between them.
 */
export function removeDeletedWebId(
  prev: SourceControlToggleState,
  sourceListId: string,
  collectionId?: string
): SourceControlToggleState {
  const webIds = prev.selectedWebCollectionIds.filter((id) => id !== sourceListId);
  const docIds = collectionId
    ? prev.selectedCollectionIds.filter((id) => id !== collectionId)
    : prev.selectedCollectionIds;

  return {
    ...prev,
    selectedWebCollectionIds: webIds,
    selectedCollectionIds: docIds,
    webCollections: webIds.length > 0 ? prev.webCollections : false,
    webCollectionsMode: webIds.length > 0 ? prev.webCollectionsMode : 'all',
    // Reset library state when no doc collections remain to avoid
    // widening evidence searches to all library documents
    myLibrary: docIds.length > 0 ? prev.myLibrary : false,
    libraryMode: docIds.length > 0 ? prev.libraryMode : 'all',
  };
}

/**
 * Drop a just-deleted source list from the persisted source-control selection.
 *
 * The body is the updater the legacy `useDeleteSourceList` ran inline in its
 * `onSuccess`, moved out verbatim when that hook crossed into
 * `@heidi/evidence` and lost the right to touch an app atom. Both surfaces
 * that delete a legacy list — the carousel card and the detail page — are
 * behind the door now too, so each takes a callback and the route that mounts
 * it binds this updater. The write still happens on the same signal it always
 * did: after the delete succeeds, never on failure.
 *
 * NOT `removeDeletedWebId`, and the difference is behavioural rather than
 * stylistic. That helper above is the UNIFIED path's updater: it also clears
 * `selectedCollectionIds` for the migrated collection and resets
 * `myLibrary` / `libraryMode` with it. A clinician with "all my library
 * documents" on and no collections picked has an empty `selectedCollectionIds`
 * — so using it here would switch My Library off on every legacy delete.
 *
 * This one touches the web selection only, and returns `prev` UNCHANGED when
 * the id was not selected, which spares every subscriber of the atom a
 * re-render. It does NOT spare the write: `sourceControlToggleAtom` is an
 * `atomWithStorage`, so a `set` persists whatever it is handed, identical
 * reference or not.
 */
export function removeDeletedSourceListId(
  prev: SourceControlToggleState,
  sourceListId: string
): SourceControlToggleState {
  const filtered = prev.selectedWebCollectionIds.filter((id) => id !== sourceListId);
  if (filtered.length === prev.selectedWebCollectionIds.length) return prev;
  return {
    ...prev,
    selectedWebCollectionIds: filtered,
    webCollections: filtered.length > 0 ? prev.webCollections : false,
    webCollectionsMode: filtered.length > 0 ? prev.webCollectionsMode : 'all',
  };
}

/**
 * Write-only atom to toggle an integration partner on/off (EVI-678).
 * @param partnerId - The partner identifier (e.g., "dynamed")
 * @param enabled - Whether to enable or disable the partner
 */
export const toggleIntegrationPartnerAtom = atom(
  null,
  (get, set, { partnerId, enabled }: { partnerId: string; enabled: boolean }) => {
    const current = get(sourceControlToggleAtom);
    const proposed = {
      ...current,
      integrationPartners: {
        ...current.integrationPartners,
        [partnerId]: enabled,
      },
    };
    set(
      sourceControlToggleAtom,
      applyChatSourceToggleGuard(
        current,
        proposed,
        IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
      )
    );
  }
);

/**
 * Write-only atom to toggle web search on/off (EVI-721).
 * Used by SourceControlPopoverV2.
 */
export const toggleWebSearchAtom = atom(null, (get, set, checked: boolean) => {
  const current = get(sourceControlToggleAtom);
  set(
    sourceControlToggleAtom,
    applyChatSourceToggleGuard(
      current,
      { ...current, webSearch: checked },
      IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED()
    )
  );
});

/**
 * Durable guard that tracks whether the primary sources defaults (Local Guidelines +
 * Primary Evidence) have ever been applied for this user. These toggles are enabled
 * by default for V1 Pro users, but only once — this flag prevents re-applying
 * defaults on subsequent page reloads and overriding explicit user choices.
 * Persisted to localStorage.
 */
export const primarySourcesDefaultsAppliedAtom = atomWithStorage<boolean>(
  'heidi:primary-sources-defaults-applied',
  false,
  undefined,
  { getOnInit: true }
);

/**
 * Durable guard that tracks whether the team-level library default has been applied.
 * When team settings have `default_library_enabled: true`, the library toggle is
 * pre-enabled for the member once. This guard prevents re-applying on subsequent
 * page reloads and overriding explicit user choices.
 * Persisted to localStorage, separate from the primary sources guard.
 */
export const libraryDefaultAppliedAtom = atomWithStorage<boolean>(
  'heidi:library-default-applied',
  false,
  undefined,
  { getOnInit: true }
);

/**
 * When set to true, opens the source control popover (e.g. from "Try now" on trial source control banner).
 * SourcesButton subscribes and opens the popover, then resets to false.
 */
export const openSourceControlPopoverAtom = atom<boolean>(false);

/**
 * One-shot trigger to open the My Library submenu inside the source control popover.
 * Set to true to programmatically expand it (e.g., from gamify trial task action).
 */
export const openMyLibrarySubmenuAtom = atom<boolean>(false);

// Selection mode: "default" = auto selection, "preset" = using toggles, "custom" = using source list
// Default is 'default' - Heidi automatically selects best sources
export type SourceSelectionMode = 'default' | 'preset' | 'custom';
export const sourceSelectionModeAtom = atom<SourceSelectionMode>('default');

// Preset toggles state - tracks which presets are enabled (independent from custom selection)
export interface PresetSelection {
  primaryEvidence: boolean;
  localGuidelines: boolean;
  webSearch: boolean;
}
export const presetSelectionAtom = atom<PresetSelection>({
  primaryEvidence: true,
  localGuidelines: true,
  webSearch: true,
});
